import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { basic3rdGraphData } from "../../../../services/webServices";
import { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";

const getMonthRange = () => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const startDate = new Date(currentYear, currentMonth - 5, 1);

  const startMonthStr = `${months[startDate.getMonth()]} ${startDate.getFullYear()}`;
  const endMonthStr = `${months[currentMonth]} ${currentYear}`;

  return `${startMonthStr} - ${endMonthStr}`;
};

const BasicChartComponent = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await basic3rdGraphData();
        let sortedData = response.data.basicDashboardData2.sort(
          (a, b) => parseInt(b.total_tokens, 10) - parseInt(a.total_tokens, 10)
        );
        setDashboardData(sortedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const serviceNames = dashboardData.map((item) => item.capability_name);
  const totalTokens = dashboardData.map((item) =>
    parseInt(item.total_tokens, 10)
  );

  const options = {
    tooltip: {
      trigger: "axis",
      textStyle: { color: "#000000" },
      axisPointer: {
        type: "shadow",
      },
      formatter: (params) => {
        const { name, value } = params[0];
        return `<b>${name}</b><br/>Total Tokens Used: <b>${value.toLocaleString()}</b>`;
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "8%",
      top: "5%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: serviceNames,
      axisLabel: {
        fontSize: 8,
        fontWeight: "normal",
        interval: 0,
        rotate: 40,
        margin: 10,
        padding: [0, 0, 0, 8],
        formatter: (value) => value,
      },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: "#616161ff" } },
    },
    yAxis: {
      type: "value",
      min: 0,
      axisLabel: {
        fontSize: 11,
      },
      splitLine: {
        lineStyle: {
          color: "#e0e0e0",
        },
      },
    },
    series: [
      {
        name: "Total Tokens Used",
        type: "bar",
        data: totalTokens,
        barWidth: 30,
        itemStyle: {
          borderRadius: 0,
          color: (params) => {
            const colors = [
              "#00325A",
              "#A1C9FF",
              "#0077CC",
              "#00487F",
              "#680019",
              "#FFB3B6",
              "#93000A",
              "#FFDAD6",
              "#6366F1",
              "#F97316",
            ];
            return colors[params.dataIndex % colors.length];
          },
        },
        emphasis: {
          itemStyle: {
            opacity: 0.9,
          },
        },
      },
    ],
  };

  return (
    <Card
      sx={{
        backgroundColor: "#F8F9FF",
        borderRadius: "12px",
        boxShadow: `4px 4px 10px #A4A4A44D, -4px -4px 10px #A4A4A44D`,
        p: { xs: 1.5, md: 2 },
        minHeight: 350,
        maxHeight: { xs: "auto", md: 500 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Top Capabilities
            <span
              style={{
                fontSize: "0.75rem",
                marginLeft: "12px",
                color: "#9C9C9C",
                fontWeight: "bold",
              }}
            >
              Platform Usage
            </span>
          </Typography>
        </Box>

        <Divider
          sx={{
            mt: 1,
            mb: 3,
            width: "100%",
            borderColor: "#e0e0e0",
          }}
        />

        <Box
          sx={{
            width: "100%",
            height: { xs: 260, md: 360 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 0, md: 2 },
          }}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <ReactECharts
              option={options}
              style={{ height: "100%", width: "100%" }}
              className="chart-container"
            />
          )}
        </Box>

        <Box mt={2} textAlign="center">
          <Typography
            variant="caption"
            sx={{ color: "#9c9c9c", fontWeight: "bold" }}
          >
            {getMonthRange()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BasicChartComponent;
